import { BookOpen, Bot, Clock3, GitBranch, ListTree, Network } from "lucide-react";
import { ArtifactKindEnum } from "@tubo/api-models/business/kb/knowledge-compilation.enums";

/** 编译中心展示的六条业务路径（topicIndex 为内部向量索引，不进产物导航） */
export type CompilationKind = Exclude<ArtifactKindEnum, "topicIndex">;

export interface CompilationKindMeta {
  kind: CompilationKind;
  label: string;
  description: string;
  icon: typeof ListTree;
  /** 详情面板/图标 chip 的路径着色（chart 色板，六类一眼可区分）。 */
  colorClass: string;
}

export const COMPILATION_KIND_META: CompilationKindMeta[] = [
  { kind: ArtifactKindEnum.TreeProjection, label: "结构树", description: "按章节层级浏览节点摘要与证据", icon: ListTree, colorClass: "bg-chart-1/10 text-chart-1" },
  { kind: ArtifactKindEnum.Timeline, label: "时间线", description: "按时间顺序查看事件与来源", icon: Clock3, colorClass: "bg-chart-4/10 text-chart-4" },
  { kind: ArtifactKindEnum.DiscoveryGraph, label: "发现图谱", description: "探索实体关系与证据链", icon: Network, colorClass: "bg-chart-3/10 text-chart-3" },
  { kind: ArtifactKindEnum.MindMap, label: "知识导图", description: "以分层画布浏览知识节点", icon: GitBranch, colorClass: "bg-chart-5/10 text-chart-5" },
  { kind: ArtifactKindEnum.Wiki, label: "Wiki 页面", description: "阅读按章节生成的知识页面", icon: BookOpen, colorClass: "bg-chart-2/10 text-chart-2" },
  { kind: ArtifactKindEnum.Skills, label: "Agent 技能", description: "面向智能体的技能说明：何时使用与检索指引", icon: Bot, colorClass: "bg-primary/10 text-primary" },
];

export function compilationKindMeta(kind: string): CompilationKindMeta | undefined {
  return COMPILATION_KIND_META.find((meta) => meta.kind === kind);
}

const KIND_LABEL_MAP = new Map<string, string>(
  COMPILATION_KIND_META.map((meta): [string, string] => [meta.kind, meta.label]),
);

export function compilationKindLabel(kind: string): string {
  return KIND_LABEL_MAP.get(kind) ?? kind;
}

export function isCompilationKind(value: unknown): value is CompilationKind {
  return typeof value === "string" && KIND_LABEL_MAP.has(value);
}

export const COMPILATION_RUN_STATUS_LABELS: Record<string, string> = {
  building: "编译中",
  fullDone: "已完成",
  warnDone: "部分完成",
  failed: "失败",
  cancelled: "已取消",
};

export const COMPILATION_ARTIFACT_STATUS_LABELS: Record<string, string> = {
  active: "当前有效",
  building: "构建中",
  stale: "已过期",
  failed: "失败",
};

export const COMPILATION_SCOPE_LABELS: Record<string, string> = {
  document: "文档级",
  knowledgeBase: "知识库级",
};

export function compilationRunStatusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "building") return "default";
  if (status === "failed") return "destructive";
  if (status === "fullDone") return "secondary";
  return "outline";
}

export function compilationArtifactStatusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "building") return "default";
  if (status === "failed") return "destructive";
  if (status === "active") return "secondary";
  return "outline";
}
